import { useFormatter } from '@api/useFormatter'
import { UploadDownloadButtons } from '@components/_SiteWide/UploadStatus/UploadDownloadButtons'
import { GraviGrid, Horizontal, NotificationMessage, SelectEditor, useGridFetch } from '@gravitate-js/excalibrr'
import api from '@utils/api'
import { Button, message, Popconfirm } from 'antd'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { NumberEditor } from './cellEditors'
import { specialPayOverrideCreateConfig } from './createConfig'
import { useUserSettings } from '@hooks/settings'
import { gridViewManager } from '@/utils/grid'

const STORAGE_KEY = 'special_pay_overrides'

export function SpecialPayOverrides() {
  const { rowData, refresh, isLoading } = useGridFetch(api.getSpecialPayOverrides)
  const [selectOptions, setSelectOptions] = useState({
    payItems: [],
    drivers: [],
  })

  useEffect(() => {
    Promise.all([api.getSpecialPayItems(), api.getDriverNames()]).then(([payItemsResponse, driversResponse]) => {
      setSelectOptions({ payItems: payItemsResponse?.data, drivers: driversResponse?.data })
    })
  }, [])

  const onCreate = (newData) => {
    const driver = selectOptions.drivers?.find((d) => d.name === newData?.name)
    return api.upsertSpecialPayOverride({ ...newData, driver_id: driver?.id })
  }

  const onUpdate = (newData) => {
    const driver = selectOptions.drivers?.find((d) => d.name === newData?.driver)
    return api.upsertSpecialPayOverride({ ...newData, driver_id: driver?.id })
  }

  const onOverrideDelete = useCallback(async (props) => {
    try {
      await api.deactivateSpecialPayOverride(props?.data?.id)
      NotificationMessage('Success', 'Special pay override deleted', false)
      props?.api?.applyTransaction({ remove: [props.node] })
    } catch (error) {
      console.error(error)
      NotificationMessage('Error', 'Failed to delete special pay override', true)
    }
  }, [])

  const { formatCurrency } = useFormatter()
  const formattedUnit = formatCurrency(null, true)
  const { updateSetting } = useUserSettings()
  const columnDefs = useMemo(() => getColumnDefs(selectOptions, formattedUnit), [selectOptions, formattedUnit])
  const getRowId = useCallback((row) => row.data?.id, [])
  const agPropOverrides = useMemo(
    () => ({
      frameworkComponents: {
        select: SelectEditor,
        number: NumberEditor,
        overrideActions: (props) => {
          return (
            <Horizontal alignItems='flex-start'>
              <Popconfirm
                title='Are you sure you want to delete this override?'
                onConfirm={() => onOverrideDelete(props)}
              >
                <Button danger type='link'>
                  Delete
                </Button>
              </Popconfirm>
            </Horizontal>
          )
        },
      },
      getRowId,
    }),
    []
  )

  return (
    <GraviGrid
      storageKey={STORAGE_KEY}
      rowData={rowData}
      updateEP={onUpdate}
      loading={isLoading}
      createEP={onCreate}
      createConfig={specialPayOverrideCreateConfig}
      createSelectOptions={selectOptions}
      shouldInsertCreated
      agPropOverrides={agPropOverrides}
      columnDefs={columnDefs}
      controlBarProps={{
        title: 'Special Pay Overrides',
        actionButtons: (
          <UploadDownloadButtons
            downloadEP={api.downloadSpecialPayOverrides}
            uploadEP={api.uploadSpecialPayOverrides}
            fileName='SpecialPayOverrides.xlsx'
            refreshCallback={refresh}
          />
        ),
      }}
      onGridConfigChanged={(config) => updateSetting(`gridConfig::${STORAGE_KEY}`, config)}
      {...gridViewManager(STORAGE_KEY, updateSetting)}
    />
  )
}

const getColumnDefs = (selectOptions, formattedUnit) => [
  {
    field: 'driver',
    headerName: 'Driver Name',
    cellEditor: 'agRichSelectCellEditor',
    cellEditorPopup: true,
    flex: 3,
    cellEditorParams: {
      values: selectOptions.drivers.map((d) => d.name),
    },
  },
  {
    field: 'special_pay_item',
    headerName: 'Pay Item',
    cellEditor: 'agRichSelectCellEditor',
    flex: 3,
    cellEditorPopup: true,
    cellEditorParams: {
      values: selectOptions.payItems,
    },
  },
  {
    field: 'rate',
    headerName: 'Rate',
    cellEditor: 'number',
    valueFormatter: ({ value }) => (value && typeof value === 'number' ? `${formattedUnit}${value?.toFixed(2)}` : ''),
  },
  {
    headerName: 'Actions',
    cellRenderer: 'overrideActions',
    editable: false,
  },
]
